import { useEffect, useState } from "react";
import { Orb } from "./Orb";
import { Wordmark } from "./Wordmark";

/* The single app-wide loading screen — ported from the Claude Design mock:
 * centered animated Orb + [Gateway] wordmark + "Optimizing your agents…" with
 * cycling dots, over a bottom progress bar. This is the ONE loader shown for
 * every "app configuration is loading" state across the boot chain (auth →
 * workspace → filters); each of those stages renders this same component while
 * its own data resolves, so it reads as one continuous splash.
 *
 * Self-contained: the design's loading palette is inlined here (no dependency
 * on the Overview theme) so it can render anywhere, including before any
 * workspace/theme context exists. It reads the theme tokens with light-mode
 * fallbacks, so it follows dark mode when the root carries `.tw-app-dark`
 * (applied on load by theme.ts, before this mounts) but still renders correctly
 * when no theme class is present yet.
 *
 * Unlike the mock (a fixed 2.9s intro) our loading is data-driven, so the bar
 * eases toward — but never reaches — 90% while data is in flight; the caller
 * unmounts this the moment its data lands.
 *
 * `label` and `tagline` default to the Gateway's; the auth splash passes the
 * Harness Optimizer's when the viewer is headed there (OPSS-487). */
export function AppLoadingScreen({
  label = "Gateway",
  tagline = "Optimizing your agents",
}: {
  label?: string;
  tagline?: string;
} = {}) {
  const [progress, setProgress] = useState(8);
  const [dots, setDots] = useState("");

  useEffect(() => {
    // Ease the bar toward 90% (asymptotic — it never "completes" on its own).
    const p = setInterval(
      () => setProgress((v) => (v >= 90 ? 90 : v + (90 - v) * 0.12)),
      280,
    );
    const d = setInterval(
      () => setDots((v) => (v.length >= 3 ? "" : v + ".")),
      380,
    );
    return () => {
      clearInterval(p);
      clearInterval(d);
    };
  }, []);

  return (
    <div
      role="status"
      aria-label="Loading"
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        background: "var(--page, #FBFBFB)",
        fontFamily:
          "'Söhne','Inter','DM Sans',ui-sans-serif,system-ui,sans-serif",
      }}
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-8">
        <Orb size={64} color="#9D1D48" />
        <Wordmark
          height={26}
          label={label}
          color="var(--text, #140309)"
          bracket="var(--text-muted, #827C7E)"
        />
        <p
          className="text-[13px] tracking-wide"
          style={{
            color: "var(--text-muted, #827C7E)",
            letterSpacing: "0.06em",
          }}
        >
          {tagline}
          <span
            style={{
              display: "inline-block",
              minWidth: "1.8ch",
              textAlign: "left",
            }}
          >
            {dots}
          </span>
        </p>
      </div>
      <div
        className="h-px w-full"
        style={{ background: "var(--border, rgba(0,0,0,0.06))" }}
      >
        <div
          className="h-full"
          style={{
            width: `${progress}%`,
            background: "#9D1D48",
            transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </div>
    </div>
  );
}
